// 16³ paletted section (ENG-5) + the SINGLE source of the index formulas (ENG-7).
// A section stores a local palette of global u16 block-state ids and a per-cell
// index into it (Uint8 while the palette fits in 256 entries, promoted to Uint16
// beyond that). Sky/block light live here too. All-air sections are stored as
// `null` at the chunk level (E-2); this class only ever represents a live one.
import { WORLD } from '../config';

export const SECTION = WORLD.SECTION; // 16
export const SECTION_VOLUME = SECTION * SECTION * SECTION; // 4096
export const STATE_AIR = 0; // BLK-2

/**
 * A dimension's vertical extent (TAL-1). `minY`/`height` are the two free
 * parameters; the rest are derived and cached so the hot index math never
 * recomputes them. This is the single source of the column shape (ENG-7): every
 * subsystem that used to read the fixed `WORLD.MIN_Y/HEIGHT/…` globals now reads
 * a world/dimension's `YRange` instead. Existing dimensions carry `DEFAULT_RANGE`
 * (identical to the old constants), so their worldgen/light goldens are unchanged.
 */
export interface YRange {
  /** Lowest build Y (inclusive). */
  readonly minY: number;
  /** Block height; a multiple of 16 (whole sections). */
  readonly height: number;
  /** Highest build Y (inclusive) = minY + height − 1. */
  readonly maxY: number;
  /** Section slots per column = height / 16. */
  readonly sectionsPerColumn: number;
  /** Array-index bias so `sectionIndex = (y >> 4) + sectionYOffset` (ENG-7). */
  readonly sectionYOffset: number;
}

/** Build a `YRange` from its two free parameters, deriving the rest (TAL-1). */
export function makeRange(minY: number, height: number): YRange {
  return {
    minY,
    height,
    maxY: minY + height - 1,
    sectionsPerColumn: height >> 4,
    sectionYOffset: -(minY >> 4),
  };
}

/** The −64..319 / 24-section column all existing dimensions use (ENG-4..7). */
export const DEFAULT_RANGE: YRange = makeRange(WORLD.MIN_Y, WORLD.HEIGHT);

/** Local cell index within a section (ENG-7). x/y/z are masked to 0..15. */
export function localIndex(x: number, y: number, z: number): number {
  return ((y & 15) << 8) | ((z & 15) << 4) | (x & 15);
}

/** Section index within a chunk column for a world Y (ENG-7). The `+4` of the
 *  default column generalizes to `+range.sectionYOffset` for any dimension. */
export function sectionIndexForY(y: number, range: YRange = DEFAULT_RANGE): number {
  return (y >> 4) + range.sectionYOffset;
}

export class Section {
  /** Local palette: palette[localIndex] -> global state id. Index 0 is always air. */
  palette: number[] = [STATE_AIR];
  /** Per-cell index into `palette`. */
  indices: Uint8Array | Uint16Array = new Uint8Array(SECTION_VOLUME);
  skyLight = new Uint8Array(SECTION_VOLUME);
  blockLight = new Uint8Array(SECTION_VOLUME);
  /** Count of non-air cells; when it hits 0 the chunk frees this section (E-2). */
  nonAirCount = 0;

  private paletteLookup = new Map<number, number>([[STATE_AIR, 0]]);

  private paletteIndexOf(state: number): number {
    const existing = this.paletteLookup.get(state);
    if (existing !== undefined) return existing;
    const idx = this.palette.length;
    this.palette.push(state);
    this.paletteLookup.set(state, idx);
    // Promote Uint8 -> Uint16 once the palette can no longer be indexed by a byte.
    if (idx > 255 && this.indices instanceof Uint8Array) {
      const wider = new Uint16Array(SECTION_VOLUME);
      wider.set(this.indices);
      this.indices = wider;
    }
    return idx;
  }

  /** Global state id at a local cell index. */
  getIndex(li: number): number {
    return this.palette[this.indices[li]!]!;
  }

  /** Set the global state id at a local cell index; maintains nonAirCount. */
  setIndex(li: number, state: number): void {
    const prev = this.palette[this.indices[li]!]!;
    if (prev === state) return;
    this.indices[li] = this.paletteIndexOf(state);
    if (prev === STATE_AIR && state !== STATE_AIR) this.nonAirCount++;
    else if (prev !== STATE_AIR && state === STATE_AIR) this.nonAirCount--;
  }

  /** Rebuild a section from a flat array of 4096 global state ids (worker payload). */
  static fromStates(states: Uint16Array): Section {
    const s = new Section();
    for (let li = 0; li < SECTION_VOLUME; li++) {
      const st = states[li]!;
      if (st !== STATE_AIR) s.setIndex(li, st);
    }
    return s;
  }

  /** Flatten to a 4096-length array of global state ids (worker payload). */
  toStates(): Uint16Array {
    const out = new Uint16Array(SECTION_VOLUME);
    for (let li = 0; li < SECTION_VOLUME; li++) out[li] = this.getIndex(li);
    return out;
  }

  get(x: number, y: number, z: number): number {
    return this.getIndex(localIndex(x, y, z));
  }

  set(x: number, y: number, z: number, state: number): void {
    this.setIndex(localIndex(x, y, z), state);
  }
}
